import React, { useEffect, useMemo } from "react";
import { Alert, FormControlLabel, Grid, Stack, Switch, Typography } from "@mui/material";
import ConfigHeader from "../ConfigHeader/config-header";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { ConfigBaseProps } from "../../../Pages/Basket/basket";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { FormExists, GetFormIsValid, GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import {
  ProductField,
  SettingRootField,
  UpdateItemProductRootOnBasket,
  UpdateItemSettingsRootOnBasket,
  UpdateItemStatusOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { BasketItemStatus } from "../../../Core/Api/Types/basket-item-status";
import ConfigSaveButton from "../ConfigSaveButton/config-save-button";
import { GetOrderProductReadableType, UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import FormSection from "../../Forms/FormSection/form-section";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import ConfigFormIcon from "../ConfigFormIcon";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { OrderProfileSettingsBase } from "../../../Core/Api/Types/order-profile-settings-base";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { Licences } from "../../../Core/Api/Types/licences";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { TranskodeWatermarkVendor } from "../../../Core/Api/Types/transkode-watermark-vendor";
import { cacheService } from "../../../Core/Api/services";
import ConfigCommonSectionFormScheduling from "../ConfigCommon/config-common-section-form-scheduling";
import ConfigCommonSectionFormNexguardWatermark from "../ConfigCommon/config-common-section-form-nexguard-watermark";
import { useTranslation } from "react-i18next";

interface ConfigAntipiracyYoutubeProps extends ConfigBaseProps {}

enum TranskodeAntipiracyDeliveryMode {
  File = "File",
  Fingerprint = "Fingerprint",
  UpdateFile = "UpdateFile",
  UpdateMetadata = "UpdateMetadata",
}

interface TranskodeAntipiracy extends OrderProfileSettingsBase {
  territoriesCodes: string[];
  movieLabels: string[];
  customId: string | null;
  referenceAssetId: string | null;
  title: string | null;
  policyName: string | null;
  enableService: boolean;
  deliveryMode: TranskodeAntipiracyDeliveryMode;
  cutStartMedia: boolean;
}

const ConfigAntipiracyYoutube: React.FC<ConfigAntipiracyYoutubeProps> = (props) => {
  const { t } = useTranslation();
  const { handleSubmit, handleCancel, handleChangeProduct, item, ...otherProps } = props;
  const formId = `form-antipiracy-yt-${item.id}`;
  const formValid = useAppSelector(GetFormIsValid(formId));
  const formExists = useAppSelector(FormExists(formId));
  const dispatch = useAppDispatch();
  const user = cacheService.GetUserInfo();

  // Item is updated automatically as it's passed as prop from parent and from redux orignally
  const settings: TranskodeAntipiracy = item.selectedProduct?.settings;

  // Update settings
  const handleUpdateParam = (field: SettingRootField, value: any) => {
    dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  // Update product root base object into redux
  const handleUpdateProductRoot = (field: ProductField, value: any) => {
    dispatch(UpdateItemProductRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  const formValidTrigger = useMemo(() => {
    // In case of invalid, update item status
    if (!formValid && formExists && item.status !== BasketItemStatus.OptionsDone)
      dispatch(UpdateItemStatusOnBasket({ itemId: item.id, status: BasketItemStatus.New }));

    return formValid;
  }, [formValid]);

  // Form validator
  const formSectionTerritoriesAndLabels = useAppSelector(GetSectionIsValid(formId, "sectionTerritoriesAndLabels"));
  const formSectionYt = useAppSelector(GetSectionIsValid(formId, "sectionYoutube"));
  const formSectionDelivery = useAppSelector(GetSectionIsValid(formId, "sectionDelivery"));

  return (
    <Stack width={"100%"} {...otherProps} display={formExists ? "" : "none"}>
      <ConfigHeader
        configType={item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.SubtitleConverter}
        title={GetOrderProductReadableType(
          item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.TranscodeGeneric
        )}
        handleCancel={handleCancel}
        handleChangeProduct={handleChangeProduct}
        itemId={item.id}
      />
      <Stack spacing={1.5} width={"100%"} {...otherProps}>
        <FormSection
          icon={<ConfigFormIcon icon="language" />}
          title={t("form:section.title.territoriesAndLabels")}
          status={formSectionTerritoriesAndLabels ? "complete" : "warning"}
          statusText={formSectionTerritoriesAndLabels ? t("common:configured") : t("common:configurationRequired")}
        >
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography>{t("alert:info.pipeDelimitedListOfValuesInfo")}</Typography>
          </Alert>
          <Grid container spacing={2}>
            <Grid item md={6}>
              <FormAutocomplete
                label={t("common:territories")}
                fullWidth={true}
                freeSolo
                options={[]}
                value={settings.territoriesCodes}
                placeholder={t("field:placeholder.territoriesAddValues")}
                multiple={true}
                onChange={(v: string[]) => {
                  if (v === null || v.length === 0) {
                    handleUpdateParam(SettingRootField.AntipiracyTerritories, []);
                    return;
                  }

                  let array: string[] = v.slice(0, v.length - 1);
                  let split = v[v.length - 1].split("|");

                  split.forEach((value) => {
                    if (!settings.territoriesCodes.includes(value)) array.push(value);
                  });

                  handleUpdateParam(SettingRootField.AntipiracyTerritories, array);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.AntipiracyTerritories, []);
                }}
                getOptionLabel={(value: any) => value}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) => x.filter((a) => a?.toLowerCase().includes(s.inputValue.toLowerCase()))}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionTerritoriesAndLabels",
                  fieldId: "territories",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>

            <Grid item md={6}>
              <FormAutocomplete
                label={t("common:labels")}
                fullWidth={true}
                freeSolo
                options={[]}
                value={settings.movieLabels}
                placeholder={t("field:placeholder.territoriesAddValues")}
                multiple={true}
                onChange={(v: string[]) => {
                  if (v === null || v.length === 0) {
                    handleUpdateParam(SettingRootField.AntipiracyLabels, []);
                    return;
                  }

                  let array: string[] = v.slice(0, v.length - 1);
                  let split = v[v.length - 1].split("|");

                  split.forEach((value) => {
                    if (!settings.territoriesCodes.includes(value)) array.push(value);
                  });

                  handleUpdateParam(SettingRootField.AntipiracyLabels, array);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.AntipiracyLabels, []);
                }}
                getOptionLabel={(value: any) => value}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) => x.filter((a) => a?.toLowerCase().includes(s.inputValue.toLowerCase()))}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionTerritoriesAndLabels",
                  fieldId: "labels",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
        <FormSection
          icon={<YouTubeIcon />}
          title={t("form:section.title.youtubeRequirements")}
          status={formSectionYt ? "complete" : "warning"}
          statusText={formSectionYt ? t("common:configured") : t("common:configurationRequired")}
        >
          <Grid container spacing={2}>
            <Grid item md={12}>
              <FormInputText
                label={t("common:title")}
                fullWidth={true}
                value={settings.title}
                placeholder={t("field:placeholder.youtubeTitleName")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.AntipiracyTitle, v.target.value);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.AntipiracyTitle, "");
                }}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionYoutube",
                  fieldId: "title",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>

            <Grid item md={6}>
              <FormInputText
                label={t("field:label.customId")}
                fullWidth={true}
                value={settings.customId}
                placeholder={t("field:placeholder.youtubeCustomId")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.AntipiracyCustomId, v.target.value);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.AntipiracyCustomId, "");
                }}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionYoutube",
                  fieldId: "customId",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={6}>
              <FormAutocomplete
                label={t("common:policy")}
                fullWidth={true}
                options={[
                  "Track Short Block Long",
                  "Monetize Short - Block Long",
                  "Block in all countries",
                  "Monetize Promo contents",
                ]}
                value={settings.policyName}
                placeholder={t("field:placeholder.selectYoutubePolicy")}
                multiple={false}
                onChange={(v: string) => {
                  handleUpdateParam(SettingRootField.AntipiracyPolicyName, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.AntipiracyPolicyName, "");
                }}
                getOptionLabel={(value: any) => value}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) => x.filter((a) => a?.toLowerCase().includes(s.inputValue.toLowerCase()))}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionYoutube",
                  fieldId: "policy",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
        <FormSection
          icon={<ConfigFormIcon icon="delivery" />}
          title={t("form:section.title.youtubeDelivery")}
          status={formSectionDelivery ? "complete" : "warning"}
          statusText={formSectionDelivery ? t("common:configured") : t("common:configurationRequired")}
        >
          <Grid container spacing={2}>
            <Grid item md={6}>
              <FormAutocomplete
                label={t("field:label.deliveryMode")}
                fullWidth={true}
                options={Object.keys(TranskodeAntipiracyDeliveryMode)}
                value={settings.deliveryMode}
                placeholder={t("field:placeholder.selectDeliveryMode")}
                multiple={false}
                onChange={(v: TranskodeAntipiracyDeliveryMode) => {
                  handleUpdateParam(SettingRootField.AntipiracyDeliveryMode, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.AntipiracyDeliveryMode, null);
                }}
                getOptionLabel={(value: any) => value}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) => x.filter((a) => a?.toLowerCase().includes(s.inputValue.toLowerCase()))}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionDelivery",
                  fieldId: "mode",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>

            <Grid item md={6}>
              <FormInputText
                label={t("field:label.referenceAssetId")}
                fullWidth={true}
                value={settings.referenceAssetId || ""}
                placeholder={t("field:placeholder.youtubeReferenceAssetId")}
                disabled={
                  settings.deliveryMode !== TranskodeAntipiracyDeliveryMode.UpdateFile &&
                  settings.deliveryMode !== TranskodeAntipiracyDeliveryMode.UpdateMetadata
                }
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.AntipiracyReferenceAssetId, v.target.value);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.AntipiracyReferenceAssetId, "");
                }}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionDelivery",
                  fieldId: "refAssetId",
                  required:
                    settings.deliveryMode === TranskodeAntipiracyDeliveryMode.UpdateFile ||
                    settings.deliveryMode === TranskodeAntipiracyDeliveryMode.UpdateMetadata,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={6}>
              <Stack>
                <FormControlLabel
                  sx={{}}
                  control={
                    <Switch
                      size="small"
                      checked={settings.enableService || false}
                      disabled={
                        settings.deliveryMode !== TranskodeAntipiracyDeliveryMode.UpdateFile &&
                        settings.deliveryMode !== TranskodeAntipiracyDeliveryMode.UpdateMetadata
                      }
                      onChange={(e) =>
                        handleUpdateParam(SettingRootField.AntipiracyEnableService, e.currentTarget.checked)
                      }
                    />
                  }
                  label={t("field:label.enableContentIdService")}
                />
              </Stack>
            </Grid>
            <Grid item md={6}>
              <Stack>
                <FormControlLabel
                  sx={{}}
                  control={
                    <Switch
                      size="small"
                      checked={settings.cutStartMedia || false}
                      onChange={(e) =>
                        handleUpdateParam(SettingRootField.AntipicaryCutStartMedia, e.currentTarget.checked)
                      }
                    />
                  }
                  label={t("field:label.cutFirstCountSeconds", { count: 20 })}
                />
              </Stack>
            </Grid>
          </Grid>
        </FormSection>
        <ConfigCommonSectionFormScheduling item={item} formid={formId} />
        {UserHasLicence(Licences.WatermarkNexguardCivolution) &&
          UserHasAcl(AclTypes.aclOrderWatermarcking) &&
          (user?.customerIds === null || user?.customerIds.length === 0) && (
            <ConfigCommonSectionFormNexguardWatermark item={item} />
          )}
      </Stack>
      <ConfigSaveButton item={item} onSubmit={handleSubmit} disabled={!formValidTrigger} />
    </Stack>
  );
};

export default ConfigAntipiracyYoutube;
